import { DOCUMENT } from '@angular/common';
import { DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, ElementRef, inject, viewChild } from '@angular/core';
import { CdnRegisteryService } from '@layout/services/cdn-registery.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { USER_GUIDE_SECTIONS } from './user-guide.const';
import { UserGuideSection } from './user-guide.model';

type PdfFontKey = 'F1' | 'F2';

interface PdfLine {
  font: PdfFontKey;
  fontSize: number;
  text: string;
  x: number;
  y: number;
}

const PDF_PAGE_WIDTH = 595;
const PDF_PAGE_HEIGHT = 842;
const PDF_MARGIN_X = 48;
const PDF_MARGIN_TOP = 56;
const PDF_MARGIN_BOTTOM = 56;
const PDF_TEXT_WIDTH = PDF_PAGE_WIDTH - PDF_MARGIN_X * 2;
const PDF_BASE_LINE_GAP = 4;

@Component({
  selector: 'mh-user-guide-dialog',
  imports: [SvgIconComponent],
  templateUrl: './user-guide-dialog.component.html',
  styleUrl: './user-guide-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserGuideDialogComponent {
  private readonly document = inject(DOCUMENT);
  private readonly dialogRef = inject<DialogRef<void, UserGuideDialogComponent>>(DialogRef);
  private readonly cdnRegisteryService = inject(CdnRegisteryService);

  private readonly dialogBody = viewChild<ElementRef<HTMLElement>>('dialogBody');
  private readonly tocAnchor = viewChild<ElementRef<HTMLElement>>('tocAnchor');

  protected readonly title = 'User Guide';
  protected readonly sections = USER_GUIDE_SECTIONS;
  protected readonly closeIcon = this.cdnRegisteryService.getMedia('close');
  protected readonly exportIcon = this.cdnRegisteryService.getMedia('export');

  protected onClose(): void {
    this.dialogRef.close();
  }

  protected onExportGuide(): void {
    const pdfContent = buildUserGuidePdf(this.sections);
    const blob = new Blob([pdfContent], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = this.document.createElement('a');

    link.href = url;
    link.download = 'user-guide.pdf';

    this.document.body.appendChild(link);
    link.click();
    this.document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  protected scrollToSection(sectionId: string): void {
    const sectionElement = this.dialogBody()
      ?.nativeElement.querySelector<HTMLElement>(`[data-guide-section-id="${sectionId}"]`);

    sectionElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  protected scrollToTop(): void {
    this.tocAnchor()?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

function buildUserGuidePdf(sections: ReadonlyArray<UserGuideSection>): string {
  const pages = paginateGuideContent(sections);
  const fontRegularObjectNumber = 3;
  const fontBoldObjectNumber = 4;
  const objects: string[] = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    `<< /Type /Pages /Kids [${pages.map((_, index) => `${5 + index * 2} 0 R`).join(' ')}] /Count ${pages.length} >>`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>',
  ];

  pages.forEach((page, index) => {
    const pageObjectNumber = 5 + index * 2;
    const contentObjectNumber = pageObjectNumber + 1;
    const stream = page
      .map(
        (line) =>
          `BT\n/${line.font} ${line.fontSize} Tf\n1 0 0 1 ${line.x} ${line.y} Tm\n(${escapePdfText(line.text)}) Tj\nET`,
      )
      .join('\n');

    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PDF_PAGE_WIDTH} ${PDF_PAGE_HEIGHT}] /Resources << /Font << /F1 ${fontRegularObjectNumber} 0 R /F2 ${fontBoldObjectNumber} 0 R >> >> /Contents ${contentObjectNumber} 0 R >>`,
    );
    objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
  });

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [0];

  objects.forEach((object, index) => {
    offsets[index + 1] = pdf.length;
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefOffset = pdf.length;

  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${offset.toString().padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

  return pdf;
}

function paginateGuideContent(sections: ReadonlyArray<UserGuideSection>): PdfLine[][] {
  const pages: PdfLine[][] = [[]];
  let currentY = PDF_PAGE_HEIGHT - PDF_MARGIN_TOP;

  const pushWrappedText = (
    text: string,
    options: { font: PdfFontKey; fontSize: number; indent?: number; spacingBefore?: number },
  ): void => {
    if (!text.trim()) return;

    const indent = options.indent ?? 0;
    const wrappedLines = wrapText(text, getMaxCharactersPerLine(options.fontSize, indent));

    wrappedLines.forEach((line, index) => {
      const spacingBefore = index === 0 ? options.spacingBefore ?? 0 : 0;
      const lineHeight = options.fontSize + PDF_BASE_LINE_GAP;

      if (currentY - spacingBefore - lineHeight < PDF_MARGIN_BOTTOM) {
        pages.push([]);
        currentY = PDF_PAGE_HEIGHT - PDF_MARGIN_TOP;
      }

      currentY -= spacingBefore;
      pages.at(-1)?.push({
        font: options.font,
        fontSize: options.fontSize,
        text: line,
        x: PDF_MARGIN_X + indent,
        y: currentY,
      });
      currentY -= lineHeight;
    });
  };

  pushWrappedText('User Guide', { font: 'F2', fontSize: 20 });
  pushWrappedText(`Exported ${formatExportDate(new Date())}`, { font: 'F1', fontSize: 10, spacingBefore: 8 });

  sections.forEach((section) => {
    pushWrappedText(section.title, { font: 'F2', fontSize: 16, spacingBefore: 18 });

    if (section.intro) {
      pushWrappedText(section.intro, { font: 'F1', fontSize: 11, spacingBefore: 6 });
    }

    section.tasks.forEach((task) => {
      pushWrappedText(task.heading, { font: 'F2', fontSize: 12, spacingBefore: 12 });
      pushWrappedText(task.body, { font: 'F1', fontSize: 11, spacingBefore: 4 });

      task.bullets?.forEach((bullet) => {
        pushWrappedText(`- ${bullet}`, { font: 'F1', fontSize: 11, indent: 14, spacingBefore: 3 });
      });
    });

    if (section.table) {
      pushWrappedText(section.table.columns.join(' | '), { font: 'F2', fontSize: 10, spacingBefore: 12 });

      section.table.rows.forEach((row) => {
        pushWrappedText(row.join(' | '), { font: 'F1', fontSize: 10, spacingBefore: 4 });
      });
    }
  });

  return pages;
}

function wrapText(text: string, maxCharacters: number): string[] {
  const words = text.trim().split(/\s+/);
  const lines: string[] = [];
  let currentLine = '';

  words.forEach((word) => {
    if (word.length > maxCharacters) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = '';
      }

      let remainingWord = word;

      while (remainingWord.length > maxCharacters) {
        lines.push(remainingWord.slice(0, maxCharacters));
        remainingWord = remainingWord.slice(maxCharacters);
      }

      currentLine = remainingWord;
      return;
    }

    const nextLine = currentLine ? `${currentLine} ${word}` : word;

    if (nextLine.length > maxCharacters) {
      lines.push(currentLine);
      currentLine = word;
      return;
    }

    currentLine = nextLine;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

function getMaxCharactersPerLine(fontSize: number, indent: number): number {
  return Math.max(24, Math.floor((PDF_TEXT_WIDTH - indent) / (fontSize * 0.52)));
}

function escapePdfText(text: string): string {
  return text.replaceAll('\\', '\\\\').replaceAll('(', '\\(').replaceAll(')', '\\)');
}

function formatExportDate(date: Date): string {
  return new Intl.DateTimeFormat('en-IE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}
